<svelte:options
  customElement={{
    tag: "oc-text-field-v1",
    extend: window.__components.extend({
      formAssociated: true,
      inputBehavior: true,
    }),
    props: {
      type: { type: "String" },
      placeholder: { type: "String" },
      name: { type: "String", reflect: true }, /*                                    */
      value: { type: "String" }, /*                                     */
      hint: { type: "String" },
      validationMessage: { type: "String", attribute: "validation-message" },
      pattern: { type: "String" },
      variant: { type: "String" },
      autocomplete: { type: "String" },
      required: { type: "Boolean" },
      min: { type: "Number" },
      max: { type: "Number" },
      minlength: { type: "Number" },
      maxlength: { type: "Number" },
      hideCounter: { type: "Boolean", attribute: "hide-counter" },
      inputmode: { type: "String" },
      prefixText: { type: "String", attribute: "prefix-text" },
      suffixText: { type: "String", attribute: "suffix-text" },
      prefixIcon: { type: "String", attribute: "prefix-icon" },
      suffixIcon: { type: "String", attribute: "suffix-icon" },
      disabled: { type: "Boolean", reflect: true }, /*                                    */
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      textTransform: { type: "String", attribute: "text-transform" },
      withoutOptionalLabel: { type: "Boolean", attribute: "without-optional-label" },
    },
  }}
/>

<script lang="ts">
  import { onMount, tick } from "svelte";
  import { toggle } from "@otto-ec/global-resources/toggle";
  import {
    ariaDescriptionWithValidation,
    implicitSubmit,
    refireNonComposableNativeEvent,
    stopLabelClickPropagation,
  } from "../../../common/actions";
  import CounterV1 from "../../../common/components/CounterV1.svelte";
  import { FormLabelIconV1 } from "../../../common/components/FormLabelIcon";
  import HintV1 from "../../../common/components/form/HintV1.svelte";
  import ValidationMessageV1 from "../../../common/components/form/ValidationMessageV1.svelte";

  import type { Props } from "./TextFieldV1.types";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import { useSelectionProperties } from "./utils/useSelectionProperties.ts";

  let {
    name = undefined,
    value = "",
    hint = undefined,
    validationMessage = undefined,
    hideDetails = false,
    variant = "default",
    required = false,
    disabled = false,
    pattern = undefined,
    autocomplete = "off",
    ocAriaLabel = undefined,
    placeholder = undefined,
    type = "text",
    min = undefined,
    max = undefined,
    minlength = undefined,
    maxlength = undefined,
    hideCounter = false,
    prefixText = undefined,
    suffixText = undefined,
    prefixIcon = undefined,
    suffixIcon = undefined,
    textTransform = undefined,
    inputmode = undefined,
    withoutOptionalLabel = false,
    internals,
  }: Props & {
    internals: ElementInternals;
    hideDetails?: boolean;
  } = $props();

  const Host = $host();

  export function resetForm(): void {
    /*                            */
    value = Host.getAttribute("value") || "";
  }

  let inputElement = $state<HTMLInputElement>();

  let hasOptionalLabel = toggle.get("oc_optional_label_on_inputfields", false);
  const OPTIONAL_SUFFIX = " (optional)";
  const OPTIONAL_SUFFIX_REGEX = /\s*\(optional\)$/;
  const originalLabelTexts = new WeakMap<ChildNode, string>();

  const updateOptionalLabel = (isRequired: boolean, hideOptionalLabel: boolean): void => {
    const shouldShowOptional = !isRequired && hasOptionalLabel && !hideOptionalLabel;

    Array.from(Host.childNodes).forEach((node) => {
      const text = node.textContent;
      if (!text) return;

      const baseText = originalLabelTexts.get(node) ?? text.replace(OPTIONAL_SUFFIX_REGEX, "");
      originalLabelTexts.set(node, baseText);
      node.textContent = shouldShowOptional ? `${baseText}${OPTIONAL_SUFFIX}` : baseText;
    });
  };

  useSelectionProperties(Host, () => inputElement);

  /*                                    */
  const onInput = (): void => {
    if (textTransform === "uppercase") {
      const start = inputElement!.selectionStart;
      value = inputElement!.value.toUpperCase();
      tick().then(() => {
        /*                                                                     */
        inputElement!.setSelectionRange(start, start);
      });
    } else {
      let next = inputElement!.value;
      if (typeof maxlength === "number" && next.length > maxlength) {
        next = next.slice(0, maxlength);
        inputElement!.value = next;
      }
      /*                                                                         */
      if (["number", "integer"].includes(type)) {
        const trimmed = next.trim();
        if (trimmed.length > 0) {
          const maxLen = typeof max === "number" ? String(max).length : 0;
          const minLen = typeof min === "number" ? String(min).length : 0;
          const threshold = Math.max(maxLen, minLen);
          if (threshold === 0 || trimmed.length >= threshold) {
            const num = Number(trimmed.replace(/,/g, "."));
            if (!Number.isNaN(num)) {
              if (typeof max === "number" && num > max) next = String(max);
              if (typeof min === "number" && num < min) next = String(min);
              inputElement!.value = next;
            }
          }
        }
      }
      value = next;
    }
  };

  const beforeInput = (event: InputEvent): void => {
    /*                                          */
    if (event.data) {
      if (type === "decimal" && !/^[0-9.,]*$/.test(event.data)) {
        event.preventDefault();
      } else if (type === "integer" && !/^\d*$/.test(event.data)) {
        event.preventDefault();
      }
    }
  };

  /*                                                                          */
  let nonEmptyPlaceholder = $derived(placeholder || " ");
  /*                                    */
  const CUSTOM_TYPES = ["integer", "decimal"] as const;
  const NATIVE_TYPES = [
    "text",
    "search",
    "tel",
    "url",
    "email",
    "password",
    "date",
    "datetime-local",
    "month",
    "time",
    "week",
    "number",
    "file",
  ] as const;

  let nativeInputType = $derived(
    /*                                         */
    CUSTOM_TYPES.includes(type as (typeof CUSTOM_TYPES)[number])
      ? "number"
      : NATIVE_TYPES.includes(type as (typeof NATIVE_TYPES)[number])
        ? type
        : "text",
  );

  /*                                             */
  /*                                                           */
  const inferInputmode = (
    t: string,
  ): "text" | "numeric" | "decimal" | "tel" | "email" | "url" | "search" | undefined => {
    switch (t) {
      case "integer":
      case "number":
        return "numeric"; /*                   */
      case "decimal":
        return "decimal"; /*                         */
      case "tel":
        return "tel"; /*           */
      case "email":
        return "email"; /*                       */
      case "url":
        return "url"; /*                     */
      case "search":
        return "search"; /*              */
      case "file":
        return undefined;
      default:
        return "text";
    }
  };

  /*                                                             */
  let derivedInputmode = $derived.by(() => {
    return (typeof inputmode === "string" && inputmode.trim()) || inferInputmode(type);
  }) as "text" | "numeric" | "decimal" | "tel" | "email" | "url" | "search";

  let showCounter = $derived(typeof (minlength ?? maxlength) === "number" && !hideCounter);
  let hasDetails = $derived(validationMessage || hint || showCounter);
  let computedVariant = $derived(validationMessage ? "error" : variant);
  let mounted = $state(false);

  $effect(() => {
    internals.setFormValue(value);
  });

  onMount(() => {
    /*                                                               */
    /*                                                              */
    /*                                                  */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        mounted = true;
      });
    });
  });

  $effect(() => {
    updateOptionalLabel(required, withoutOptionalLabel);
  });
</script>

<label
  class="text-field"
  class:text-field--disabled={disabled}
  class:text-field--error={computedVariant === "error"}
  class:text-field--has-value={value}
  class:mounted
  class:text-field--success={computedVariant === "success"}
  class:text-field--warning={computedVariant === "warning"}
  use:stopLabelClickPropagation
  data-oc-floating-focus-v1-masked
>
  <input
    {autocomplete}
    bind:this={inputElement}
    class="text-field__input"
    {disabled}
    id="input"
    {max}
    {min}
    {maxlength}
    {minlength}
    {name}
    use:refireNonComposableNativeEvent={Host}
    use:implicitSubmit={internals}
    use:ariaDescriptionWithValidation={{
      validationMessage,
      hint,
      minLength: minlength,
      maxLength: maxlength,
    }}
    onbeforeinput={beforeInput}
    oninput={onInput}
    {pattern}
    placeholder={nonEmptyPlaceholder}
    type={nativeInputType}
    inputmode={derivedInputmode}
    {value}
    aria-required={required}
    aria-labelledby="text-field__label"
    spellcheck="false"
    autocorrect="off"
  />

  {#if prefixIcon}
    <oc-icon-v1 class="text-field__prefix-icon" type={prefixIcon}></oc-icon-v1>
  {/if}

  {#if prefixText}
    <small class="text-field__prefix-text">{prefixText}</small>
  {/if}

  {#if suffixText}
    <small class="text-field__suffix-text">{suffixText}</small>
  {/if}

  {#if suffixIcon}
    <oc-icon-v1 class="text-field__suffix-icon" type={suffixIcon}></oc-icon-v1>
  {/if}

  <span
    id="text-field__label"
    class="text-field__label"
    aria-label={ocAriaLabel}
    aria-hidden="true"
    data-oc-floating-focus-v1-mask-element
  >
    <svelte:element
      this={"slot"}
      onslotchange={() => {
        updateOptionalLabel(required, withoutOptionalLabel);
      }}
    />
    <FormLabelIconV1 variant={computedVariant} />
  </span>
</label>

{#if !hideDetails && hasDetails}
  <div class="text-field__details" aria-hidden="true">
    <div>
      <ValidationMessageV1 {validationMessage} />
      <HintV1 {hint} {disabled} />
    </div>
    {#if showCounter}
      <CounterV1 counterValue={value} maxCounter={maxlength} minCounter={minlength} {disabled} />
    {/if}
  </div>
{/if}

<DefaultStyles display="block" />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";
  $autofill-background-color: #f0f4fc;
  @mixin transition($properties...) {
    @media (prefers-reduced-motion: no-preference) {
      transition-property: $properties;
      transition-duration: 0.1s;
      transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    }
  }
  $gap: 8px;

  :global {
    :host {
      --oc-floating-focus-v1-preserve-outline: "";
      --oc-floating-focus-v1-full-bleed: "";

      /*                */
      --opacity: 1;
      --label-top: -0.5rem;
      --label-left: #{tokens.$oc-component-form-field-floating-label-spacing-left};
      --label-padding: 0 #{tokens.$oc-component-form-field-floating-label-inner-spacing-x};
      --label-font: #{tokens.$oc-component-form-field-floating-label-font};
    }

    .text-field {
      position: relative;
      display: grid;
      grid-template-areas: "prefix-icon prefix-text input suffix-text suffix-icon";
      grid-template-columns: auto auto 1fr auto auto auto;
      align-items: center;

      padding: tokens.$oc-component-form-field-spacing-y tokens.$oc-component-form-field-spacing-x;

      background: tokens.$oc-component-form-background-color;
      outline: 1px solid tokens.$oc-component-form-default-border-color;
      border-radius: tokens.$oc-component-form-field-border-radius;
      cursor: text;

      &:hover:not(.text-field--disabled) {
        outline-width: 2px;
      }

      &:focus-within {
        outline-width: 2px;

        &:not(.text-field--error):not(.text-field--warning):not(.text-field--success) {
          outline-color: tokens.$oc-component-form-default-border-color-focus;
        }
      }

      &__input {
        all: unset;
        grid-area: input;

        width: 100%;
        min-height: 1.5rem;

        font: tokens.$oc-component-form-field-input-font;
        color: tokens.$oc-component-form-field-input-color;

        &::placeholder {
          font: tokens.$oc-component-form-field-placeholder-font;
          color: tokens.$oc-component-form-field-placeholder-color;
          opacity: var(--opacity);
          white-space: nowrap;
          text-overflow: ellipsis;
          max-width: 100%;
        }
      }

      &.mounted .text-field__input::placeholder {
        @include transition(opacity);
      }

      &__label {
        position: absolute;
        top: var(--label-top);
        left: var(--label-left);
        padding: var(--label-padding);
        max-width: calc(100% - 2 * #{tokens.$oc-semantic-spacing-50});

        font: var(--label-font);
        background-color: tokens.$oc-component-form-background-color;
        border-radius: tokens.$oc-component-form-field-label-border-radius;
        color: tokens.$oc-component-form-default-label-color;

        /*                            */
        display: flex;
        align-items: center;
        gap: tokens.$oc-component-form-state-icon-gap-x;

        > slot {
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          user-select: none;
          cursor: text;
        }
      }

      &.mounted .text-field__label {
        @include transition(line-height, top, font-size);
      }

      &__details {
        display: flex;
        gap: 4px;
        justify-content: space-between;
        width: 100%;
      }

      &--success {
        outline-color: tokens.$oc-component-form-success-border-color;

        .text-field__label {
          color: tokens.$oc-component-form-success-label-color;
        }
      }

      &--warning {
        outline-color: tokens.$oc-component-form-warning-border-color;

        .text-field__label {
          color: tokens.$oc-component-form-warning-label-color;
        }
      }

      &--error {
        outline-color: tokens.$oc-component-form-error-border-color;

        .text-field__label {
          color: tokens.$oc-component-form-error-label-color;
        }
      }

      &--disabled {
        cursor: default;
        outline-color: tokens.$oc-component-form-disabled-border-color;
        background-color: tokens.$oc-component-form-disabled-background-color;

        .text-field__input {
          color: tokens.$oc-component-form-disabled-input-color;
        }

        .text-field__label {
          cursor: default;
          background-color: tokens.$oc-component-form-disabled-background-color;
          color: tokens.$oc-component-form-disabled-label-color;
        }
      }

      .text-field__prefix-icon {
        grid-area: prefix-icon;
      }

      .text-field__prefix-text {
        grid-area: prefix-text;
      }

      .text-field__suffix-icon {
        grid-area: suffix-icon;
      }

      .text-field__suffix-text {
        grid-area: suffix-text;
      }

      .text-field__prefix-text,
      .text-field__prefix-icon {
        opacity: var(--opacity);
        margin-right: $gap;
      }

      .text-field__suffix-text,
      .text-field__suffix-icon {
        opacity: var(--opacity);
        margin-left: $gap;
      }

      &.mounted .text-field__prefix-text,
      &.mounted .text-field__prefix-icon,
      &.mounted .text-field__suffix-text,
      &.mounted .text-field__suffix-icon {
        @include transition(opacity);
      }

      .text-field__prefix-text,
      .text-field__suffix-text {
        font: tokens.$oc-component-form-field-input-font;
        user-select: none;
      }

      &:has(
        > :not([slot]):not(
            :where(:not(:placeholder-shown), :autofill, :-webkit-autofill, :focus-visible)
          )
      ) {
        --opacity: 0;
        --label-top: #{tokens.$oc-component-form-field-spacing-y};
        --label-left: #{tokens.$oc-semantic-spacing-50};
        --label-padding: 0 #{tokens.$oc-semantic-spacing-25};
        --label-font: #{tokens.$oc-component-form-label-font};
      }

      /*                           */
      &__input {
        &:autofill {
          -webkit-box-shadow: 0 0 0 50px $autofill-background-color inset;
          -webkit-text-fill-color: tokens.$oc-component-form-field-input-color;
        }
      }

      /*                                 */
      /*                                                                   */
      &__input {
        /*                             */
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        /*         */
        &[type="number"] {
          -moz-appearance: textfield;
        }
      }

      /*                                                               */
      &[type="date"] {
        &::-webkit-datetime-edit,
        &::-webkit-datetime-edit-fields-wrapper,
        &::-webkit-datetime-edit-day-field,
        &::-webkit-datetime-edit-month-field,
        &::-webkit-datetime-edit-year-field {
          padding: 0;
        }
      }
    }

    /*                                                                                 */
    :has(.text-field__input:autofill) {
      background: $autofill-background-color;
    }
  }
</style>
